import { Component, EventEmitter, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { SubSink } from 'subsink';
import { Subscription, distinctUntilChanged } from 'rxjs';
import { isNumber } from '../../utility';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace FormGenerator {

  export interface Form {
    id?: number;
    fields: FormGenerator.Field[];
    options: Options;
    layoutType?: 'p-form-widget';
    col?: number;
    header?: string;
    onSave?: (event: any) => void;
  }

  export interface Options {
    isLoading: boolean;
    isExternal: boolean;
    enableButtons: boolean;
    accessViewPermissions?: string[];
    initiallyEnabled?: boolean;
    canEdit?:boolean;
  }

  export interface FieldOptions {
    disabled?: boolean;
    showRequiredError?: boolean;
    requiredErrorMessage?: string;
    minMaxErrorMessage?: string;
  }

  export interface InputOptions extends FieldOptions {
    type?: string;
    min?: number;
    max?: number;
    maxLength?: number;
    addon?: string;
    pKeyFilter?: 'pint' | 'int' | 'pnum' | 'num' | 'money' | 'hex' | 'email' | 'alpha' | 'alphanum';
  }

  export interface InputGroupOptions extends FieldOptions {
    showRequiredError?: boolean;
    requiredErrorMessage?: string;
  }

  export interface HorizontalListOptions extends FieldOptions {
    list?: { class?: string, value: string }[];
  }

  export interface MultiSelectOptions extends FieldOptions {
    options?: { label: string, value: string }[];
  }

  export interface DropdownOptions extends FieldOptions {
    options?: string[];
  }

  export interface CheckboxOptions extends FieldOptions {
    label?: string;
  }

  export interface Field {
    id?: number;
    fieldType: 'textarea' | 'dropdown' | 'input' | 'calendar' | 'inputMask' | 'checkbox' | 'inputGroup' | 'multiSelect' | 'horizontalTextList' | 'radioButton' | 'radioButtonGroup';
    col?: number;
    colOptions?: { xs?: number, sm?: number, md?: number, lg?: number, xl?: number };
    name?: string;
    value?: unknown;
    label?: string;
    showIf?: (form: FormGroup) => boolean;
    valueChanges?: (form: FormGroup, value: unknown, field: Field) => void;
    validators?: ValidatorFn[];
    fields?: Field[];
    header?: string;
    initiallyDisabled?: boolean;
    options?: FieldOptions;
  }
}

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'form-generator',
    templateUrl: './form-generator.component.html',
    styleUrls: ['./form-generator.component.scss'],
    standalone: false
})
export class FormGeneratorComponent implements OnInit, OnDestroy {
  @Input() form: FormGenerator.Form;

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  private onSave = new EventEmitter<unknown>();
  private originalFormValue: unknown;
  formGroup: FormGroup;
  private subs = new SubSink();
  private onSaveSubscription: Subscription;

  //#region Lifecycle
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {

    // if you already have a subscription unsubscribe and use the new method
    if (this.onSaveSubscription) {
      this.onSaveSubscription.unsubscribe();
    }

    // when onSave is emitted, call the onSave function part of the form
    this.subs.sink = this.onSaveSubscription = this.onSave.subscribe(m => {
      this.form.onSave(m);
    });

    // missing options add them
    if (!this.form.options) {
      this.form.options = {
        isLoading: false,
        isExternal: false,
        enableButtons: false,
      };
    }

    // create the form
    this.createForm();

    if (this.form.options.initiallyEnabled) {
      this.formGroup.enable();
    }
    else {
      this.formGroup.disable();
    }
  }
  //#endregion

  //#region Form
  updateForm(key: string, value: string): void {
    // key doesn't exist, nothing to do
    if (!this.formGroup.get(key)) {
      return;
    }

    // update the form group value
    this.formGroup.get(key).setValue(value);
    this.formGroup.get(key).updateValueAndValidity();
  }

  createForm(): void {
    this.formGroup = new FormGroup([]);

    // use the input forms fields
    this.form.fields.forEach(ff => {

      if (ff.fields) {
        // add subFields
        ff.fields.forEach(f => {
          this.addControl(f);
        });
      }
      else {
        this.addControl(ff);
      }
    });
  }

  addControl(field: FormGenerator.Field) {
    // add values if you have any initially
    const control = new FormControl(field.value, field.validators);

    // if it has a value changes method use it
    if (field.valueChanges) {
      this.subs.sink = control.valueChanges.pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
        .subscribe(value => {
          field.valueChanges(this.formGroup, value, field);
        });
    }

    if (field.initiallyDisabled) {
      control.disable();
    }

    this.formGroup.addControl(field.name, control);
  }
  //#endregion

  //#region UI
  inputGroupOnKeyPress(field: FormGenerator.Field, e: KeyboardEvent): boolean {

    const options = field.options as FormGenerator.InputOptions;
    const min = options?.min, max = options?.max;

    // nothing to validate
    if (!min && !max) {
      return true;
    }

    const v = e.key;

    if (!isNumber(v)) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    else {
      const selection = window.getSelection();
      const selectedText = selection.toString();
      const f = this.formGroup.get(field.name);
      let v2 = f.value as string;

      // wipe out selected text
      if (selectedText && selectedText == v2) {
        selection.deleteFromDocument();
        selection.empty();
        this.updateForm(field.name, null);
        v2 = f.value as string;
      }

      const v3 = `${v2}${v}`;
      const length = (max.toString()).length;

      if (v2 && v3.length > length) {
        e.preventDefault();
        e.stopPropagation();
        // trying to go above, set to the max
        this.formGroup.get(field.name).setValue(max);
        return false;
      }

      const n2 = parseInt(v3) || 0;

      const result = (n2 >= min && n2 <= max) || v3.length < (max.toString()).length;
      if (!result) {
        e.preventDefault();
        e.stopPropagation();
        // trying to go above, set to the max
        this.formGroup.get(field.name).setValue(max);
      }

      return result;
    }
  }

  getFormLayoutType(form: FormGenerator.Form): unknown {
    const result = {};
    if (!form.layoutType) {
      return;
    }

    result[`${form.layoutType}`] = true;

    return result;
  }

  getFormCol(form: FormGenerator.Form): unknown {
    const result = {};
    const value = form.col || 12;
    result[`col-${value}`] = true;

    return result;
  }

  getFieldCol(field: FormGenerator.Field): unknown {

    const result = {};
    const value = field.col || 12;
    result[`col-${value}`] = true;

    if (field.colOptions) {
      if (field.colOptions.xs) {
        result[`xs:col-${field.colOptions.xs}`] = true;
      }
      if (field.colOptions.sm) {
        result[`sm:col-${field.colOptions.sm}`] = true;
      }
      if (field.colOptions.md) {
        result[`md:col-${field.colOptions.md}`] = true;
      }
      if (field.colOptions.lg) {
        result[`lg:col-${field.colOptions.lg}`] = true;
      }
      if (field.colOptions.xl) {
        result[`xl:col-${field.colOptions.xl}`] = true;
      }
    }

    return result;
  }

  isLoading(value: boolean) {
    this.form.options.isLoading = value;
  }

  isRequired(field: FormGenerator.Field): boolean {
    if (field.fields) {
      let hasValidator = false;

      // check if any of the fields are required, if they are
      field.fields.forEach(f => {
        hasValidator = this.formGroup.get(f.name)?.hasValidator(Validators.required);
      });

      return hasValidator;
    }

    return this.formGroup.get(field.name)?.hasValidator(Validators.required);
  }

  getErrorMessage(field: FormGenerator.Field, name: string, errorType: 'maxlength' | 'required' | 'min' | 'max'): string {
    const control = this.formGroup.get(name);
    switch (errorType) {
      case 'maxlength':
        return `Must be less than ${control.getError('maxlength')?.requiredLength} characters`;
      case 'required':
        return field.options.requiredErrorMessage;
      case 'min':
      case 'max':
        return field.options.minMaxErrorMessage;
      default:
        return null;
    }
  }
  //#endregion

  //#region Menu Buttons
  cancel(): void {
    // reset to the forms original value
    this.formGroup.setValue(this.originalFormValue);
    this.formGroup.updateValueAndValidity();

    this.formGroup.markAsPristine();

    // disable to keep it from validating while not in edit mode
    this.formGroup.disable();
  }

  edit(): void {
    // create a copy of the forms original value
    this.originalFormValue = { ...this.formGroup.value };

    // enable editing
    this.formGroup.enable();
  }

  save(): void {

    // emit onSave with the forms value
    this.onSave.emit(this.formGroup.value);

    // disable to prevent changes
    this.formGroup.disable();
  }
  //#endregion
}
